import type { ColumnDef, SortingState } from "@tanstack/react-table"
import Dialog from "@/v2-ui/dialog"
import Table from "@/v2-backoffice-client/components/table"
import type { PortfolioCustomerRow } from "@/v2-backoffice-client/portfolio/portfolio.customerTable.columns"

export type PortfolioCustomerListDialogProps<
  TRow extends PortfolioCustomerRow,
> = {
  open: boolean
  onOpenChange(open: boolean): void
  title: string
  /** One plain sentence explaining what the list and its numbers mean. */
  description: string
  /** Stable id, so column sizes survive between openings. */
  tableId: string
  rows: TRow[]
  /**
   * The table's columns, built once at module scope with
   * `createCustomerColumns` so every opening shares the same instances.
   */
  columns: ColumnDef<TRow, unknown>[]
  /** The order the list opens in. */
  initialSorting: SortingState
}

// The "show everything" dialog behind movers, segments and the capability
// lists: the standard Backoffice table, so every number has a header, every
// column sorts, and the search box narrows the list. Names open the
// account's Activity tab.
function PortfolioCustomerListDialog<TRow extends PortfolioCustomerRow>(
  props: PortfolioCustomerListDialogProps<TRow>,
) {
  const {
    open,
    onOpenChange,
    title,
    description,
    tableId,
    rows,
    columns,
    initialSorting,
  } = props

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="w-11/12 max-w-screen-lg" align="top">
        <Dialog.Header>
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description>{description}</Dialog.Description>
        </Dialog.Header>

        <Table<TRow>
          className="max-h-[65vh]"
          tableId={tableId}
          data={rows}
          columns={columns}
          initialSorting={initialSorting}
          getRowId={(row) => row.customer.externalCustomerId}
        >
          <Table.Toolbar>
            <div className="flex-1" />
            <Table.Search placeholder="Search customers…" />
          </Table.Toolbar>
          <Table.Content
            countLabel="customers"
            renderEmpty={
              <p className="py-6 text-center text-sm text-muted-foreground">
                No customers match the search.
              </p>
            }
          />
        </Table>
      </Dialog.Content>
    </Dialog>
  )
}

export default PortfolioCustomerListDialog
